import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { ChevronLeft } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';

import { Campaign, CampaignType, Charity, Event, Pagination, Project, ProjectType } from '../../Shared/types';
import { Icon } from '../../Shared/UI/Icon';
import { useCharityStyle } from '../../Shared/useCharityStyle';
import AddCampaignForm from '../Campaign/AddCampaignForm';
import { SharedProps } from '../types';
import CharityLayout from '../UI/CharityLayout';
import Container from '../UI/Container';
import { Footer } from '../UI/Footer';
import { NavBar } from '../UI/NavBar';
import { ProjectLabel } from './ProjectLabel';
import SelectProjectForm from './SelectProjectForm';

export interface CreateCampaignPageProps extends SharedProps {
  project: Project | null;
  event: Event | null;
  events: Pagination<Event>;
  team: Campaign | null;
  teams: Campaign[];
  charity: Charity;
  redirect?: (url: string) => void;
}

export default function CreateCampaignPage({
  project,
  event,
  events,
  team,
  teams,
  charity,
  auth: {
    user,
  },
  params,
}: CreateCampaignPageProps) {
  const { t } = useTranslation();

  useCharityStyle(charity);

  const memberId = typeof params.member_id === 'string' ? params.member_id : null;
  const type = params.type === CampaignType.Individual || params.type === CampaignType.Team ? params.type : null;

  const requireSelectProject = !project || (project.type === ProjectType.Event && !event);
  const allowCreateCampaign = project ? project.open && !project.ended : event && !event.ended;

  return (
    <>
      <NavBar user={user} charity={charity} showCampaignButton={false} />

      <Container>
        {team && (
          <div>
            <Link href={team.url} className="inline-flex space-x-1">
              <Icon className="mt-1">
                <ChevronLeft />
              </Icon>
              <span>
                {team.title}
              </span>
            </Link>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl sm:text-2xl">
              {t('frontend:start_a_campaign')}
            </h1>
            {params.source === 'create' && (
              <span className="text-sm whitespace-nowrap text-slate-500 mt-0.5">
                {t('frontend:step_x_of_y', { x: (project || event) ? 3 : 2, y: 3 })}
              </span>
            )}
          </div>

          {(project || event) && (
            <ProjectLabel project={project} event={event} />
          )}
        </div>

        <div className="p-4 sm:p-8 bg-gradient-to-tl from-project/10 to-project/5 rounded-xl space-y-4">
          <div className={classNames((team?.thumbnail_url || charity.logo_url) && 'grid gap-3 grid-cols-[60px_1fr]')}>
            {team?.thumbnail_url && (
              <div className="shrink-0 h-[54px]">
                <img src={team.thumbnail_url} alt={team.title} className="object-cover w-full h-full rounded-lg" />
              </div>
            )}
            {!team?.thumbnail_url && charity.logo_url && (
              <div className="flex items-center justify-center p-3 bg-white rounded-lg shadow-sm shrink-0 h-[54px]">
                <img src={charity.logo_url} alt={charity.title} className="block w-full" />
              </div>
            )}
            <div className="space-y-1 min-w-0">
              <h2 className="text-base font-medium leading-snug whitespace-pre-line pt-0.5">
                {team && (
                  <Trans i18nKey="frontend:join_team_name" values={{ team: team.title }}>
                    <strong className="font-bold" />
                  </Trans>
                )}
                {!team && (
                  <strong className="font-bold">
                    {charity.title}
                  </strong>
                )}
              </h2>
              {!team && (
                <div className="whitespace-pre-line text-slate-600">
                  {project?.description || charity.description}
                </div>
              )}
            </div>
          </div>
        </div>

        {requireSelectProject && (
          <SelectProjectForm
            charity={charity}
            events={events}
            projectType={project?.type}
            params={params}
          />
        )}

        {!requireSelectProject && allowCreateCampaign && (
          <AddCampaignForm
            project={project}
            event={event}
            type={type}
            team={team}
            teams={teams}
            memberId={memberId}
            charity={charity}
            user={user}
          />
        )}

        {!requireSelectProject && !allowCreateCampaign && (
          <div className="text-slate-500">
            {t('frontend:project_closed_message')}
          </div>
        )}
      </Container>

      <Footer charity={charity} />
    </>
  );
}

CreateCampaignPage.layout = (page: ReactNode) => <CharityLayout>{page}</CharityLayout>;
